import redux from './redux'
import React from 'react'
import { createRoot } from 'react-dom/client'
import Modal from 'react-modal'
import { theme } from './pergas-components/theme/index.jsx'
import { ThemeProvider } from '@pergas-common/pergas-components'
import { Provider } from 'react-redux'
import './index.css'
import App from './App.js'
import msal from './lib/msal.js'
import storage from './redux/storage.js'
import { isValidToken } from './util.js'

Modal.setAppElement('#root-modal')
const REDIRECT_THROTTLE_MS = 10000

function clearAuthHashIfPresent () {
  const { hash, pathname, search } = window.location
  if (!hash) return

  const authHashPattern = /^#(?:code|error|state|session_state|client_info)=/i
  if (authHashPattern.test(hash) || (hash.includes('code=') && hash.includes('state='))) {
    window.history.replaceState({}, document.title, `${pathname}${search}`)
  }
}

function canInitiateRedirectLogin () {
  const interactionStatus = window.sessionStorage.getItem('msal.interaction.status')
  if (interactionStatus) {
    console.info('skipping loginRedirect: interaction already in progress')
    return false
  }

  const lastRedirectTs = Number(window.sessionStorage.getItem('pds.lastRedirectTs') || 0)
  if ((Date.now() - lastRedirectTs) < REDIRECT_THROTTLE_MS) {
    console.warn('skipping loginRedirect: throttled to avoid redirect loop')
    return false
  }

  window.sessionStorage.setItem('pds.lastRedirectTs', String(Date.now()))
  return true
}

const start = ({ idToken = '', token = '' }) => {
  function Main () {
    return (
      <Provider store={redux.store}>
        <ThemeProvider theme={theme}>
          <App idToken={idToken} token={token} />
        </ThemeProvider>
      </Provider>
    )
  }
  createRoot(document.getElementById('root')).render(<Main />)
}

/**
 * Main login method to receive id token
 * 1. when the user hasn't logged in before, or
 * 2. when ssoSilent() fails
 */
function redirectLogin () {
  if (!canInitiateRedirectLogin()) return

  try {
    console.info('initiating redirect method')
    msal.loginRedirect()
  } catch (err) {
    // TODO this is a fatal error -> render Error page with details
    console.error('msal.loginRedirect failed', err)
  }
}

async function bootstrapAuth () {
  try {
    const tokenResponse = await msal.handleRedirectPromise()
    if (tokenResponse !== null) {
      console.info('got id token from redirect method')
      clearAuthHashIfPresent()
      start({ idToken: tokenResponse.idToken })
      return
    }
  } catch (err) {
    // TODO this is a fatal error -> render Error page with details
    console.error('msal.handleRedirectPromise failed', err)
  }

  const { token, email } = redux.store.getState().login.userData

  if (isValidToken(token)) {
    console.info('found cached valid id token')
    clearAuthHashIfPresent()
    start({ token })
    return
  }

  const loginHint = email
  if (typeof loginHint === 'string') {
    console.info('msal.ssoSilent() with login hint', loginHint)
    const silentRequest = { loginHint }

    try {
      const result = await msal.ssoSilent(silentRequest)
      clearAuthHashIfPresent()
      start({ idToken: result.idToken })
      return
    } catch (err) {
      console.error('msal.ssoSilent failed', err)
      storage.deleteUserData()
      redirectLogin()
      return
    }
  }

  redirectLogin()
}

bootstrapAuth()
